import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";

import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STAGETYPE, STATUSARRAY } from "../../_contstants/common";
import { clearLeadStageDetails } from "./leadStageSlice";
import {
  createLeadStage,
  getAllActiveLeadStage,
  getAllLeadStage,
  getLeadStageByID,
  updateLeadStage,
} from "./services/leadStage.services";
import IconButton from "../../components/common/IconButton";

const CreateLeadStage: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const LeadStageDetail = useAppSelector(
    (state) => state.leadStage.LeadStageDetail,
  );
  const LeadStageID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.leadStage.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearLeadStageDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (LeadStageID) {
      dispatch(getLeadStageByID(LeadStageID));
    }
  }, [LeadStageID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [LeadStageDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!LeadStageID) {
        const insertPayload = {
          LeadStage: values?.LeadStage ? values?.LeadStage : "",
          LeadStageType: values.LeadStageType ? values.LeadStageType : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createLeadStage(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearLeadStageDetails());
          if (isIndirect) {
            dispatch(getAllActiveLeadStage(""));
          } else {
            dispatch(getAllLeadStage());
          }
        }
      } else {
        const editPayload = {
          ID: LeadStageID,
          LeadStage: values?.LeadStage ? values?.LeadStage : "",
          LeadStageType: values.LeadStageType ? values.LeadStageType : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateLeadStage(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearLeadStageDetails());
          dispatch(getAllLeadStage());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    dispatch(clearLeadStageDetails());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={LeadStageDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem className="mui-drawer-header">
                <Typography.h4 className="m-0">
                  {LeadStageID ? "Update Stage" : "Add Stage"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="LeadStage"
                  label="Stage Name"
                  placeholder="Stage Name"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="LeadStageType"
                  label="Stage Type"
                  placeholder="Stage Type"
                  astrike={true}
                  validator={requiredValidator}
                  component={FormSelectionField}
                  options={STAGETYPE?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="IsActive"
                  label="Status"
                  placeholder="i.e. Active"
                  astrike={true}
                  validator={requiredValidator}
                  component={FormSelectionField}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={LeadStageID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateLeadStage;
